import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { AppLoader } from '@/shared/components/feedback/AppLoader';

import { useSetupRoute } from '../hooks/useSetupRoute';

/**
 * Puts a person who has not finished setting up the LinkedIn agent back on the step they stopped
 * at, instead of showing them an empty overview.
 *
 * <h2>Why a gate on the route and not a handler on the sidebar link</h2>
 *
 * The previous system decided where the sidebar link led when it was clicked. That covers the one
 * link and nothing else: a bookmark, the browser's back button, a link in an email and the banners
 * all reached the empty overview. A gate on the route covers every way in, and cannot race the
 * click it used to be bound to.
 *
 * <p>`replace`, so the back button does not return to a page that would send the person on again.
 * While the three questions are being asked the page shows the application's loader, so an empty
 * overview never flashes before the redirect.
 */
export function SetupGate({ children }: { children: ReactNode }) {
  const { isLoading, route } = useSetupRoute();

  if (isLoading) return <AppLoader />;
  if (route) return <Navigate to={route} replace />;
  return <>{children}</>;
}
